import { useMemo, useState } from "react";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  type ColumnFiltersState,
  type ColumnOrderState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  type Header,
} from "@tanstack/react-table";
import {
  CaretUp,
  CaretDown,
  Eye,
  X,
  DotsSixVertical,
} from "@phosphor-icons/react";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { LoteSVG } from "../../types/sitePlan";

interface Props {
  lotes: LoteSVG[];
  selectedId: number | null;
  onRowClick: (lote: LoteSVG) => void;
  onClose?: () => void;
}

const fmtCRC = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-CR", {
        style: "currency",
        currency: "CRC",
        maximumFractionDigits: 0,
      }).format(n);

const fmtDate = (s: string | null | undefined) => {
  if (!s) return "—";
  const datePart = s.split("T")[0];
  const [y, m, d] = datePart.split("-");
  if (!y || !m || !d) return "—";
  return `${d}/${m}/${y}`;
};

const fmtNum = (n: number | null | undefined, suffix = "") =>
  n == null ? "—" : `${n.toFixed(1)}${suffix}`;

function diasContratoColor(d: number | null): string {
  if (d == null) return "transparent";
  if (d < -60) return "#22c55e";
  if (d <= -30) return "#f59e0b";
  if (d <= 0) return "#dc2626";
  return "#ef4444";
}

const STORAGE_KEY_VISIBILITY = "siteplan-table-columns";
const STORAGE_KEY_ORDER = "siteplan-table-order";

// ============================================================
// Sortable header (drag handle + título + sort + filtro)
// ============================================================
function SortableHeader({ header }: { header: Header<LoteSVG, unknown> }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: header.column.id });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: "relative",
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <th
      ref={setNodeRef}
      style={style}
      className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider"
    >
      <div className="flex items-center gap-1">
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="flex h-5 w-4 cursor-grab items-center justify-center opacity-40 hover:opacity-100 active:cursor-grabbing"
          style={{ color: "#747B86" }}
          aria-label="Reordenar columna"
          onClick={(e) => e.stopPropagation()}
        >
          <DotsSixVertical size={14} weight="bold" />
        </button>

        {/* Título + sort */}
        <div
          onClick={header.column.getToggleSortingHandler()}
          className="flex flex-1 cursor-pointer items-center gap-1 select-none hover:opacity-70"
          style={{ color: "#747B86", letterSpacing: "0.4px" }}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
          {header.column.getIsSorted() === "asc" && (
            <CaretUp size={10} weight="bold" />
          )}
          {header.column.getIsSorted() === "desc" && (
            <CaretDown size={10} weight="bold" />
          )}
        </div>
      </div>

      {/* Filtro por columna */}
      {header.column.getCanFilter() && (
        <input
          type="text"
          value={(header.column.getFilterValue() as string) ?? ""}
          onChange={(e) => header.column.setFilterValue(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          placeholder="Filtrar…"
          className="mt-1 w-full border px-2 py-0.5 text-xs font-normal normal-case"
          style={{
            borderColor: "#EBEBEB",
            borderRadius: 4,
            color: "#5D636C",
            letterSpacing: "0",
          }}
        />
      )}
    </th>
  );
}

// ============================================================
// Tabla principal
// ============================================================
export function LoteTable({ lotes, selectedId, onRowClick, onClose }: Props) {
  const columns = useMemo<ColumnDef<LoteSVG>[]>(
    () => [
      {
        id: "lote",
        accessorKey: "lote",
        header: "Lote",
        cell: ({ row }) => (
          <span
            className="inline-block px-2 py-0.5 text-xs font-bold text-white"
            style={{
              backgroundColor: row.original.colorHex,
              borderRadius: 4,
              minWidth: 50,
              textAlign: "center",
            }}
          >
            {row.original.lote}
          </span>
        ),
      },
      {
        id: "estado",
        accessorKey: "estado",
        header: "Estado",
      },
      {
        id: "area",
        accessorKey: "area",
        header: "Área",
        cell: ({ getValue }) => fmtNum(getValue<number>(), " m²"),
      },
      {
        id: "frente",
        accessorKey: "frente",
        header: "Frente",
        cell: ({ getValue }) => fmtNum(getValue<number>(), " m"),
      },
      {
        id: "fondo",
        accessorKey: "fondo",
        header: "Fondo",
        cell: ({ getValue }) => fmtNum(getValue<number>(), " m"),
      },
      {
        id: "modelo",
        header: "Modelo",
        accessorFn: (row) => row.modeloCaso ?? row.modeloTramitado ?? "",
        cell: ({ getValue }) => getValue<string>() || "—",
      },
      {
        id: "precioVenta",
        accessorKey: "precioVenta",
        header: "Precio Venta",
        cell: ({ getValue }) => fmtCRC(getValue<number>()),
      },
      {
        id: "precioCasa",
        accessorKey: "precioCasa",
        header: "Precio Casa",
        cell: ({ getValue }) => fmtCRC(getValue<number>()),
      },
      {
        id: "precioLote",
        accessorKey: "precioLote",
        header: "Precio Lote",
        cell: ({ getValue }) => fmtCRC(getValue<number>()),
      },
      {
        id: "vendedor",
        accessorKey: "vendedor",
        header: "Vendedor",
        cell: ({ getValue }) => getValue<string>() || "—",
      },
      {
        id: "formalizador",
        accessorKey: "formalizador",
        header: "Formalizador",
        cell: ({ getValue }) => getValue<string>() || "—",
      },
      {
        id: "cliente",
        accessorKey: "cliente",
        header: "Cliente",
        cell: ({ getValue }) => getValue<string>() || "—",
      },
      {
        id: "banco",
        accessorKey: "banco",
        header: "Banco",
        cell: ({ row }) =>
          row.original.banco ? (
            <span
              className="inline-block px-2 py-0.5 text-xs font-bold text-white"
              style={{
                backgroundColor: row.original.colorHEXBan ?? "#1B3A6B",
                borderRadius: 4,
              }}
            >
              {row.original.banco}
            </span>
          ) : (
            "—"
          ),
      },
      {
        id: "fechaReserva",
        accessorKey: "fechaReserva",
        header: "Reserva",
        cell: ({ getValue }) => fmtDate(getValue<string>()),
      },
      {
        id: "fechaForOPF",
        header: "For/PF",
        accessorFn: (row) => row.fechaFormalizacion ?? row.fechaPF,
        cell: ({ getValue }) => fmtDate(getValue<string>()),
      },
      {
        id: "fechaEntrega",
        accessorKey: "fechaEntrega",
        header: "Entrega",
        cell: ({ getValue }) => fmtDate(getValue<string>()),
      },
      {
        id: "diasContrato",
        accessorKey: "diasContrato",
        header: "Días Contrato",
        cell: ({ getValue }) => {
          const d = getValue<number | null>();
          if (d == null) return "—";
          return (
            <span
              className="inline-block px-2 py-0.5 text-xs font-bold text-white"
              style={{
                backgroundColor: diasContratoColor(d),
                borderRadius: 4,
              }}
            >
              {d} días
            </span>
          );
        },
      },
    ],
    [],
  );

  const defaultVisibility: VisibilityState = {
    lote: true,
    estado: true,
    area: true,
    frente: false,
    fondo: false,
    modelo: true,
    precioVenta: true,
    precioCasa: false,
    precioLote: false,
    vendedor: true,
    formalizador: false,
    cliente: true,
    banco: true,
    fechaReserva: false,
    fechaForOPF: false,
    fechaEntrega: false,
    diasContrato: false,
  };

  const defaultOrder: ColumnOrderState = columns.map((c) => c.id as string);

  const [sorting, setSorting] = useState<SortingState>([
    { id: "lote", desc: false },
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY_VISIBILITY);
        return saved ? { ...defaultVisibility, ...JSON.parse(saved) } : defaultVisibility;
      } catch {
        return defaultVisibility;
      }
    },
  );

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_ORDER);
      if (!saved) return defaultOrder;
      const parsed: string[] = JSON.parse(saved);
      // Asegurar que columnas nuevas no presentes en el storage queden al final
      const fresh = defaultOrder.filter((id) => !parsed.includes(id));
      return [...parsed.filter((id) => defaultOrder.includes(id)), ...fresh];
    } catch {
      return defaultOrder;
    }
  });

  const [showColumnMenu, setShowColumnMenu] = useState(false);

  const handleVisibilityChange = (
    updater: VisibilityState | ((old: VisibilityState) => VisibilityState),
  ) => {
    setColumnVisibility((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try {
        localStorage.setItem(STORAGE_KEY_VISIBILITY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const handleOrderChange = (next: ColumnOrderState) => {
    setColumnOrder(next);
    try {
      localStorage.setItem(STORAGE_KEY_ORDER, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  const table = useReactTable({
    data: lotes,
    columns,
    state: { sorting, columnFilters, columnVisibility, columnOrder },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: handleVisibilityChange,
    onColumnOrderChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(columnOrder) : updater;
      handleOrderChange(next);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // Sensores con un mínimo de movimiento para no interferir con click en sort
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = columnOrder.indexOf(active.id as string);
    const newIndex = columnOrder.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;
    handleOrderChange(arrayMove(columnOrder, oldIndex, newIndex));
  };

  return (
    <div
      className="flex h-full w-full flex-col bg-white"
      style={{ fontFamily: "Roboto, sans-serif" }}
    >
      {/* Header del drawer */}
      <div
        className="flex items-center justify-between border-b px-4 py-3"
        style={{ borderColor: "#EBEBEB" }}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold" style={{ color: "#5D636C" }}>
            Lotes
          </h3>
          <span className="text-sm" style={{ color: "#747B86" }}>
            {table.getFilteredRowModel().rows.length} de {lotes.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setShowColumnMenu((o) => !o)}
              className="flex items-center gap-1 border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-gray-50"
              style={{ borderColor: "#D9D9D9", borderRadius: 8, color: "#5D636C" }}
            >
              <Eye size={14} weight="bold" />
              Columnas
            </button>
            {showColumnMenu && (
              <>
                <div
                  className="fixed inset-0 z-30"
                  onClick={() => setShowColumnMenu(false)}
                />
                <div
                  className="absolute right-0 top-full z-40 mt-1 max-h-80 w-56 overflow-y-auto bg-white py-2 shadow-lg"
                  style={{ borderRadius: 8, border: "1px solid #EBEBEB" }}
                >
                  {table.getAllLeafColumns().map((column) => (
                    <label
                      key={column.id}
                      className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm transition-colors hover:bg-gray-50"
                      style={{ color: "#5D636C" }}
                    >
                      <input
                        type="checkbox"
                        checked={column.getIsVisible()}
                        onChange={column.getToggleVisibilityHandler()}
                      />
                      {flexRender(column.columnDef.header, {} as never)}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center transition-colors hover:bg-gray-100"
              style={{ borderRadius: 8, color: "#5D636C" }}
              aria-label="Cerrar"
            >
              <X size={18} weight="bold" />
            </button>
          )}
        </div>
      </div>

      {/* Tabla con scroll */}
      <div className="flex-1 overflow-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="w-full text-sm">
            <thead
              className="sticky top-0 z-10 bg-white"
              style={{ borderBottom: "2px solid #EBEBEB" }}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  <SortableContext
                    items={headerGroup.headers.map((h) => h.column.id)}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header) => (
                      <SortableHeader key={header.id} header={header} />
                    ))}
                  </SortableContext>
                </tr>
              ))}
            </thead>
            <tbody>
              
  {table.getRowModel().rows.map((row) => {
    const isSelected = row.original.idLote === selectedId;
    return (
      <tr
        key={row.id}
        onClick={() => onRowClick(row.original)}
        className="cursor-pointer transition-colors"
        style={{
          backgroundColor: isSelected ? "#EBEBEB" : "transparent",
          // Borde negro grueso (igual al lote seleccionado en el plano)
          outline: isSelected ? "3px solid #000000" : "none",
          outlineOffset: "-3px",
          // Para que el outline quede por encima de las filas vecinas
          position: "relative",
          zIndex: isSelected ? 1 : 0,
          borderBottom: isSelected ? "none" : "1px solid #EBEBEB",
        }}
        onMouseEnter={(e) => {
          if (!isSelected) e.currentTarget.style.backgroundColor = "#F8F8F8";
        }}
        onMouseLeave={(e) => {
          if (!isSelected) e.currentTarget.style.backgroundColor = "transparent";
        }}
      >
        {row.getVisibleCells().map((cell, idx) => (
          <td
            key={cell.id}
            className="px-3 py-2"
            style={{
              color: "#5D636C",
              position: idx === 0 ? "relative" : undefined,
            }}
          >
            {/* Accent verde lima en el lateral izquierdo (solo en la primera celda) */}
            {idx === 0 && isSelected && (
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  backgroundColor: "#ADD010",
                  zIndex: 2,
                }}
              />
            )}
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
    );
  })}
</tbody>
          </table>
        </DndContext>
        {table.getRowModel().rows.length === 0 && (
          <div
            className="flex h-32 items-center justify-center text-sm"
            style={{ color: "#747B86" }}
          >
            Sin resultados.
          </div>
        )}
      </div>
    </div>
  );
}